import { Component, OnInit, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { delay, SubscriptionLike } from 'rxjs';
import { AuthService } from '@tempton/ngx-msal';

import {
  ActivityReportsService,
  PhotoDocumentService,
  SessionStorageService,
  NotificationService,
  SidebarStorageService,
  UserService,
  AssignmentService,
  ToasterService,
  InvoicesService,
  FailedErpService,
  VacationRequestListService,
  MileageMoneyListService,
  ReferralsService,
  ContractExtensionsService,
  ExternalEmployeesService,
} from '@shared/services';

import { User } from '@shared/factories';
import { SidebarRoute, SidebarRouteExtended } from '@shared/models';
import { environment } from 'environments/environment';

@Component({
  selector:  'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls:  ['./sidebar.component.sass'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SidebarComponent implements OnInit {
  mainNavItems:    SidebarRouteExtended[];
  botomNavItems:   SidebarRouteExtended[];
  activeTab:       SidebarRouteExtended;
  activeApp:       string;
  sidebarMinified: boolean;
  user:            User;

  openActivityReportsCount:          number;
  clarificationReportsCount:         number;
  approvedReportsCount:              number;
  failedExportActivityReportsCount:  number;

  activePhotoDocumentsCount:         number;
  unconfirmedEBSCount:               number;
  expiringSoonAssignmentsCount:      number;

  activeInvoicesCount:               number;
  archivedInvoicesCount:             number;

  approvedVacationRequestsCount:     number;
  awaitingVacationRequestsCount:     number;
  rejectedVacationRequestsCount:     number;
  failedExportVacationRequestsCount: number;

  awaitingMileageMoneyReportsCount:  number;

  activeReferralsCount:              number;
  contractExtensionsCount:           number;
  externaEmployeesCount:             number;

  public getReportsCountFn: Function = this.getReportsCount.bind(this);

  collapsePreview: boolean = false;

  subscriptions:    SubscriptionLike[] = [];
  appSubscriptions: SubscriptionLike[] = [];

  production: boolean = environment.production;

  version:      string;
  buildHash:    string;

  debugMode:    boolean;
  debugCounter: number = 0;

  @HostListener('document:click', ['$event'])
  preventSidebarClose(event) {
    if (window.innerWidth < 768) {
      if (event.target.closest('#portal-sidebar')) event.stopPropagation();
      else if (event.target.closest('aside')) this.closeSidebar();
    }
    if (event.target.closest('.sidebartoggler')) {
      this.toggleSidebar();
      this.collapsePreview = false;
    }
  }
  constructor(
    private router:                     Router,
    private authService:                AuthService,
    private assignmentService:          AssignmentService,
    private activityReportsService:     ActivityReportsService,
    private photoDocumentService:       PhotoDocumentService,
    private invoicesService:            InvoicesService,
    private vacationRequestListService: VacationRequestListService,
    private mileageMoneyListService:    MileageMoneyListService,
    private referralsService:           ReferralsService,
    private contractExtensionsService:  ContractExtensionsService,
    private externalEmployeesService:   ExternalEmployeesService,
    private failedErpService:           FailedErpService,
    private sessionStorageService:      SessionStorageService,
    private sidebarStorageService:      SidebarStorageService,
    private notificationService:        NotificationService,
    private toasterService:             ToasterService,
    public  userService:                UserService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(this.sidebarStorageService.sidebarItemsSubscribe.subscribe(items => {
      this.mainNavItems  = items.filter(i => !i.bottom);
      this.botomNavItems = items.filter(i =>  i.bottom);
    }));
    this.sessionStorageService.activeApp.subscribe(app => {
      if (this.activeApp !== app) this.resetSubscriptions();
      this.activeApp = app;
    })
    this.subscriptions.push(this.userService.currentUser.subscribe(user => {
      this.user    = user;
      this.version = user?.latest_releases?.find(r =>
        user.isInternal && r.application === 'internal_portal' ||
        user.isCustomer && r.application === 'customer_portal'
      )?.version;

      this.buildHash = this.sessionStorageService.buildHashValue?.substring(0,4);
    }));
    this.subscriptions.push(this.sessionStorageService.activeTab.subscribe(tab => this.activeTab = tab));
    //avoid ExpressionChangedAfterItHasBeenCheckedError
    this.subscriptions.push(this.sidebarStorageService.sidebarState.pipe(delay(1)).subscribe(status => {
        this.sidebarMinified = status === 'mini';
    }));
    this.prepareAppSubscriptions();
    this.prepareGoBackHandler();
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (window.innerWidth < 768) this.closeSidebar();
    this.collapsePreview = false;
  }

  ngAfterViewInit() {
    this.onWindowResize();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
    this.stopAppSubscriptions();
  }

  private prepareAppSubscriptions(): void {
    if  (this.sessionStorageService.isTimeTracking) this.prepareTimeTrackingSubscriptions();
    else if (this.sessionStorageService.isInvoices) this.prepareInvoicesSubscriptions();
  }

  private stopAppSubscriptions(): void {
    this.appSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.appSubscriptions = [];
  }

  resetSubscriptions(): void {
    this.stopAppSubscriptions();
    this.prepareAppSubscriptions();
  }

  private prepareTimeTrackingSubscriptions(): void {
    this.appSubscriptions.push(this.sidebarStorageService.approvedReportsCount.subscribe(             items => this.approvedReportsCount              = items));
    this.appSubscriptions.push(this.sidebarStorageService.openActivityReportsCount.subscribe(         items => this.openActivityReportsCount          = items));
    this.appSubscriptions.push(this.sidebarStorageService.clarificationReportsCount.subscribe(        items => this.clarificationReportsCount         = items));
    this.appSubscriptions.push(this.sidebarStorageService.failedExportActivityReportsCount.subscribe( items => this.failedExportActivityReportsCount  = items));

    this.appSubscriptions.push(this.sidebarStorageService.activePhotoDocumentsCount.subscribe(        items => this.activePhotoDocumentsCount         = items));
    this.appSubscriptions.push(this.sidebarStorageService.unconfirmedEBSCount.subscribe(              items => this.unconfirmedEBSCount               = items));
    this.appSubscriptions.push(this.sidebarStorageService.expiringSoonAssignmentsCount.subscribe(     items => this.expiringSoonAssignmentsCount      = items));

    this.appSubscriptions.push(this.sidebarStorageService.approvedVacationRequestsCount.subscribe(    items => this.approvedVacationRequestsCount     = items));
    this.appSubscriptions.push(this.sidebarStorageService.awaitingVacationRequestsCount.subscribe(    items => this.awaitingVacationRequestsCount     = items));
    this.appSubscriptions.push(this.sidebarStorageService.rejectedVacationRequestsCount.subscribe(    items => this.rejectedVacationRequestsCount     = items));
    this.appSubscriptions.push(this.sidebarStorageService.failedExportVacationRequestsCount.subscribe(items => this.failedExportVacationRequestsCount = items));

    this.appSubscriptions.push(this.sidebarStorageService.awaitingMileageMoneyReportsCount.subscribe( items => this.awaitingMileageMoneyReportsCount  = items));
    this.appSubscriptions.push(this.sidebarStorageService.activeReferralsCount.subscribe(             items => this.activeReferralsCount              = items));
    this.appSubscriptions.push(this.sidebarStorageService.contractExtensionsCount.subscribe(          items => this.contractExtensionsCount           = items));
    this.appSubscriptions.push(this.sidebarStorageService.externaEmployeesCount.subscribe(            items => this.externaEmployeesCount             = items));
  }

  private prepareInvoicesSubscriptions(): void {
    this.appSubscriptions.push(this.sidebarStorageService.activeInvoicesCount.subscribe(  items => this.activeInvoicesCount   = items));
    this.appSubscriptions.push(this.sidebarStorageService.archivedInvoicesCount.subscribe(items => this.archivedInvoicesCount = items));
  }

  getReportsCount(navItem: SidebarRouteExtended): number | string {
    if      (navItem.counter)                       return this.getCounterNumber(navItem.counter);
    else if (navItem.identifier ===    'dashboard') return this.collectChildCounters(navItem.children);
    else if (navItem.identifier === 'vr-dashboard') return this.collectChildCounters(navItem.children);
  }

  private getCounterNumber(counter: string): number | string {
    switch (counter) {
      case 'approved':     { return this.approvedReportsCount;                                     };
      case 'open':         { return this.openActivityReportsCount;                                 };
      case 'rejected':     { return this.clarificationReportsCount;                                };
      case 'photo':        { return this.activePhotoDocumentsCount;                                };
      case 'ebs':          { return this.expiringSoonAssignmentsCount || this.unconfirmedEBSCount; };
      case 'ar-failed':    { return this.failedExportActivityReportsCount;                         };
      case 'vr-failed':    { return this.failedExportVacationRequestsCount;                        };
      case 'active':       { return this.activeInvoicesCount;                                      };
      case 'archived':     { return this.archivedInvoicesCount;                                    };
      case 'vr-approved':  { return this.approvedVacationRequestsCount;                            };
      case 'vr-awaiting':  { return this.awaitingVacationRequestsCount;                            };
      case 'vr-rejected':  { return this.rejectedVacationRequestsCount;                            };
      case 'mm-open':      { return this.awaitingMileageMoneyReportsCount;                         };
      case 'referrals':    { return this.activeReferralsCount;                                     };
      case 'contract-ext': { return this.contractExtensionsCount;                                  };
      case 'externals':    { return this.externaEmployeesCount;                                    };

      case 'ar-total':     { return this.approvedReportsCount          + this.openActivityReportsCount      + this.clarificationReportsCount;     };
      case 'vr-total':     { return this.approvedVacationRequestsCount + this.awaitingVacationRequestsCount + this.rejectedVacationRequestsCount; };
      case 'employees':    { return this.activeReferralsCount          + this.externaEmployeesCount         + this.expiringSoonAssignmentsCount + this.unconfirmedEBSCount };
      default:
        counter && console.debug("Unknown counter name: " + counter);
        return 0;
    };
  }

  private collectChildCounters(children: SidebarRoute[]): number {
    return children.reduce((sum, val) => sum += +!!this.getCounterNumber(val.counter), 0);
  }

  changeDashboard(navItem: SidebarRouteExtended): any {
    if (navItem.identifier === 'logout') return this.authService.signOut$();
    if (navItem.externalPath) return window.open(navItem.externalPath, '_blank', 'noopener, noreferrer');
    if (navItem.path) this.router.navigateByUrl(`${this.sessionStorageService.activeAppValue}/${navItem.path}`);

    this.reloadList(navItem);
    this.handleArchive(navItem);

    if (window.innerWidth < 1280) this.closeSidebar();
  }

  private reloadList(navItem: SidebarRouteExtended): void {
    if (this.sessionStorageService.isTimeTracking && (navItem.counter || navItem.identifier === 'dashboard') || navItem.identifier === 'vr-dashboard') {
      // this.notificationService.wait();
      switch (navItem.identifier) {
        case 'ar-approved':
        case 'ar-open':
        case 'ar-rejected':
        case 'dashboard':     { this.activityReportsService.forceReload();     break; };

        case 'vr-approved':
        case 'vr-open':
        case 'vr-rejected':
        case 'vr-dashboard':  { this.vacationRequestListService.forceReload(); break; };

        case 'ar-failed-erp':
        case 'vr-failed-erp': { this.failedErpService.forceReload();           break; };

        case 'photo-doc':     { this.photoDocumentService.forceReload();       break; };
        case 'ebs-list':      { this.assignmentService.forceReload();          break; };
        case 'mm-list':       { this.mileageMoneyListService.forceReload();    break; };
        case 'referrals':     { this.referralsService.forceReload();           break; };
        case 'contract-ext':  { this.contractExtensionsService.forceReload();  break; };
        case 'ee-list':       { this.externalEmployeesService.forceReload();   break; };
        default: console.error('Unknown NavItem identifier ' + navItem.identifier);
      }
    } else if (this.sessionStorageService.isInvoices) this.invoicesService.forceReload(navItem.filter);
  }

  private handleArchive(navItem: SidebarRouteExtended): void {
    if (this.sessionStorageService.isTimeTracking && navItem.identifier.includes('archive')) {
      let tab = navItem.path.includes('search-archiv?tab=') ? navItem.path.split('?tab=')[1] : 'ar';
      this.sessionStorageService.changeArchiveTogglers(tab);
      this.sessionStorageService.changeAndResetActiveTab(`archive-${tab}`);
    } else this.sessionStorageService.changeAndResetActiveTab(navItem.identifier);
  }

  private prepareGoBackHandler(): void {
    this.router.events.subscribe((event: NavigationStart) => {
      if (event.navigationTrigger === 'popstate' && event.url.includes('list')) {
        let navItem: SidebarRouteExtended;
        let url = event.url.split(`/${this.activeApp}/`)[1];

        if (url) navItem = this.sidebarStorageService.sidebarItems
        .reduce((sum, i) =>sum = [...sum, ...(i.children?.length ? i.children : [i])], [])
        .find(item => item.path === url);

        if (navItem) this.sessionStorageService.changeAndResetActiveTab(navItem.identifier);
      }
    });
  }

  closeSidebar(): void {
    const sidebar = document.getElementById('portal-sidebar');
    sidebar?.classList.add('mini-sidebar');
    sidebar?.classList.remove('mini-sidebar-xs');
    this.sidebarStorageService.changeSidebarState('mini');
  }

  private toggleSidebar(): void {
    const sidebar = document.getElementById('portal-sidebar');
    if (sidebar?.classList.contains('mini-sidebar-xs')) sidebar.classList.remove('mini-sidebar-xs');
    else sidebar?.classList.toggle('mini-sidebar');
    this.sidebarStorageService.toggleSidebarState();
  }

  openDebug(): void {
    if (this.debugMode) {
      this.debugCounter--;
      if (this.debugCounter === 0) this.debugMode = false;
    } else {
      this.debugCounter++;
      if (this.debugCounter === 10) this.debugMode = true;
    }
  }

  openToast(e) {
    e.stopPropagation();
    this.toasterService.openPreferences();
  }

}
